import cn from 'classnames';
import { useColorContrast } from 'hooks/color-contrast-checker';
import { Controller, useWatch } from 'react-hook-form';

import { ColorContrastCard } from 'components/ColorContrastCard/ColorContrastCard';
import DisplayBox from 'components/DisplayBox/DisplayBox';
import { EditableSettings } from 'components/EditableSettings/EditableSettings';
import {
  Button,
  ColorInput,
  ImagePicker,
  Input,
  InputGroup,
  Select,
  Textarea,
} from 'components/FormComponents';

export const General = ({
  brand,
  hasVolunteer = false,
  errors,
  control,
  register,
  sendBrandPreview = () => {},
  readOnly = false,
  backgroundColor = 'background-secondary',
}) => {
  const brandActiveValue = useWatch({ control, name: 'brand_active' });
  const bannerLogoActiveValue = useWatch({ control, name: 'banner_logo_active' });
  const colorActiveValue = useWatch({ control, name: 'color_active' });
  const colorButtonValue = useWatch({ control, name: 'color_button' });
  const colorButtonTextValue = useWatch({ control, name: 'color_button_text' });
  const colorLinkValue = useWatch({ control, name: 'color_link' });
  const emailLogoAlignmentValue = useWatch({ control, name: 'email_logo_alignment' });
  const { ratio } = useColorContrast(colorLinkValue, '#fff');

  return (
    <div className={cn('u-padding', backgroundColor)}>
      <div className={cn('card', !readOnly ? 'card-with-border u-padding' : '')}>
        <EditableSettings
          title="Use customized branding"
          open={brandActiveValue}
          toggleable
          register={register}
          name="brand_active"
          className="u-margin-0">
          {brandActiveValue && (
            <>
              <InputGroup title="Name">
                <Input
                  register={register}
                  validators={{ required: true }}
                  name="brand_name"
                  placeholder="Checkmate"
                  error={errors?.brand_name?.type}
                />
              </InputGroup>
              <InputGroup title="Email">
                <Input
                  register={register}
                  name="brand_email"
                  placeholder="no-reply@checkmate.tech"
                  error={brand.brand_email && !brand.signature_id && 'Connection problem'}
                />
              </InputGroup>
              <InputGroup title="Logo - ideal size is 700x600 pixels">
                <Controller
                  control={control}
                  name="brand_logo_id"
                  render={({ field: { onChange, value } }) => (
                    <ImagePicker onChange={onChange} value={value} />
                  )}
                />
              </InputGroup>
              <InputGroup title="Email logo alignment">
                <Select
                  name="email_logo_alignment"
                  register={register}
                  value={emailLogoAlignmentValue ?? 'left'}
                  useDefault>
                  <Select.Item value="left">Left</Select.Item>
                  <Select.Item value="center">Center</Select.Item>
                  <Select.Item value="right">Right</Select.Item>
                </Select>
              </InputGroup>
              <InputGroup
                title="Candidate Introduction Text"
                description="Keep it short and concise. Maximum 500 characters.">
                <Textarea
                  name="candidate_introduction_text"
                  register={register}
                  maxLength={500}
                  placeholder="This system is a secure verification platform designed to simplify the process for employers and employees."
                  inputProps={{ rows: 10 }}
                />
              </InputGroup>
              {hasVolunteer && (
                <InputGroup
                  title="Volunteer Introduction Text"
                  description="Keep it short and concise. Maximum 500 characters.">
                  <Textarea
                    name="volunteer_introduction_text"
                    register={register}
                    maxLength={500}
                    placeholder="This system is a secure verification platform designed to simplify the process for organisations and volunteers."
                    inputProps={{ rows: 10 }}
                  />
                </InputGroup>
              )}
              <EditableSettings
                title="Email Banner - ideal size is 560x190 pixels"
                open={bannerLogoActiveValue}
                toggleable
                register={register}
                className="mb-6"
                name="banner_logo_active">
                <Controller
                  control={control}
                  name="banner_logo_id"
                  render={({ field: { onChange, value } }) => (
                    <ImagePicker onChange={onChange} value={value} />
                  )}
                />
              </EditableSettings>
              <EditableSettings
                title="Brand Colours"
                open={colorActiveValue}
                toggleable
                register={register}
                className="mb-6"
                name="color_active">
                <DisplayBox show={true} className="u-margin-bottom" white>
                  <ColorContrastCard
                    foreground={colorButtonTextValue}
                    background={colorButtonValue}
                  />
                </DisplayBox>
                <InputGroup title="Button color">
                  <ColorInput
                    name="color_button"
                    register={register}
                    initialColor={colorButtonValue}
                  />
                </InputGroup>
                <InputGroup title="Button text color">
                  <ColorInput
                    name="color_button_text"
                    register={register}
                    initialColor={colorButtonTextValue}
                  />
                </InputGroup>
                <InputGroup title="Link color">
                  <ColorInput name="color_link" register={register} initialColor={colorLinkValue} />
                  {ratio < 4.5 && (
                    <span className="u-font-size-x-small text-error">
                      Link color may be hard to read on white background based on WCAG standards.
                      Choose a color with better contrast for readability.
                    </span>
                  )}
                </InputGroup>
              </EditableSettings>
              {!readOnly && (
                <div className={cn('u-flex-box', 'u-width-100', 'u-flex-justify-end')}>
                  <Button
                    type="secondary"
                    className="u-margin-bottom--small"
                    onClick={sendBrandPreview}>
                    Preview Your Brand
                  </Button>
                </div>
              )}
            </>
          )}
        </EditableSettings>
      </div>
    </div>
  );
};
